import React from "react";
import {
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "../../fields/MoneyField";
import { SectionAccordion } from "./SectionAccordion";
import LandscapeIcon from "@mui/icons-material/Landscape";
import {
  LotExtraSectionProps,
  ChangeCountLotExtraSectionProps,
} from "../../../../types";

export interface LotExtrasSectionComponentProps {
  state: LotExtraSectionProps;
  setState: (state: LotExtraSectionProps) => void;
  formErrors: Record<string, string>;
  updateField: (field: string, value: string) => void;
  total: number;
  subtotalLaborMaterial: number;
  mode: "edit" | "review";
  pageVariant: "create" | "edit" | "view";
  initialState?: LotExtraSectionProps;
  changeCount?: ChangeCountLotExtraSectionProps;
  forceMobile?: boolean;
}

export function LotExtrasSection({
  state,
  setState,
  formErrors,
  updateField,
  total,
  subtotalLaborMaterial,
  mode,
  pageVariant,
  initialState,
  changeCount,
  forceMobile,
}: LotExtrasSectionComponentProps) {
  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const viewFieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "rgba(0,0,0,0.45)" },
    "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(0,0,0,0.45)" },
  };
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  const dollarAdornment = (
    <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment>
  );

  function initialText(val: number | undefined): string {
    return val === undefined ? "" : val.toString().replace(/^0+/, "");
  }

  function changeCountText(count: number): string {
    return count > 1 ? ` (${count})` : "";
  }

  return (
    <SectionAccordion
      id="lotExtras"
      title="Lot Extras"
      icon={
        <LandscapeIcon
          fontSize="large"
          sx={{ color: "#ACACAC" }}
        />
      }
      total={total}
      forceMobile={forceMobile}
    >
      {/* Lot Extras (Exit Strategy) */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Lot Extras(Exit Strategy)
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.LotExtras)}{" "}
            {changeCountText(changeCount?.LotExtras ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            disabled
            value={state.LotExtras ?? ""}
            InputProps={{ startAdornment: dollarAdornment }}
            sx={viewFieldSx}
          />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={
              state.LotExtras === undefined
                ? ""
                : state.LotExtras.toString().replace(/^0+/, "")
            }
            onChange={(e) => updateField("LotExtras", e.target.value)}
            error={formErrors["LotExtras"] !== ""}
            helperText={formErrors["LotExtras"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Lot Extras(Exit Strategy)"
            value={state.LotExtras ?? null}
            onChange={(v) => updateField("LotExtras", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["LotExtras"] !== ""}
            helperText={formErrors["LotExtras"]}
          />
        )}
      </Grid>

      {/* Total Lot Extras */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Total Lot Extras(Exit Strategy)
        </Typography>
      </Grid>
      <Grid item xs={fieldXs}>
        <TextField
          variant="filled"
          size="small"
          fullWidth
          value={total || 0}
          inputProps={{ min: 0 }}
          disabled
          InputProps={{ startAdornment: dollarAdornment }}
        />
      </Grid>

      {/* Subtotal Labor & Material */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}
        mt={5}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Subtotal Labor & Material
        </Typography>
      </Grid>
      <Grid item xs={fieldXs} mt={isMobile ? 0 : 5}>
        <TextField
          variant="filled"
          size="small"
          type="number"
          inputProps={{ min: 0 }}
          value={subtotalLaborMaterial || 0}
          disabled
          fullWidth
          InputProps={{ startAdornment: dollarAdornment }}
        />
      </Grid>
    </SectionAccordion>
  );
}
